/* eslint-disable @typescript-eslint/no-explicit-any */
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui-library/tooltip';
import { iSelectProps } from '@/types/components';
import { cn } from '@/utils';
import { ChevronDown, ChevronUp, Info, X } from 'lucide-react';
import Select from 'node_modules/react-select/dist/declarations/src/Select';
import { forwardRef, memo, useRef } from 'react';
import type { GroupBase } from 'react-select';
import ReactSelect from 'react-select';
import errorIcon from '/assets/error-icon.svg';

const IsRequiredIcon = (
  <svg width="5" height="5" viewBox="0 0 4 4" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.98852 3.564C1.70052 3.564 1.43652 3.496 1.19652 3.36C0.964516 3.216 0.776516 3.028 0.632516 2.796C0.496516 2.556 0.428516 2.292 0.428516 2.004C0.428516 1.564 0.580516 1.192 0.884516 0.888C1.18852 0.584 1.55652 0.432 1.98852 0.432C2.43652 0.432 2.81252 0.584 3.11652 0.888C3.42052 1.192 3.57252 1.564 3.57252 2.004C3.57252 2.444 3.42052 2.816 3.11652 3.12C2.81252 3.416 2.43652 3.564 1.98852 3.564Z" fill="#C21808" />
  </svg>
);

const FloatingDropdown = forwardRef<Select<unknown, boolean, GroupBase<unknown>>, iSelectProps>((props, ref) => {
  const { name, label = 'Select Value', width, isRequired, options, tooltipText, infoText, errors, ...rest } = props;
  const id = useRef(Date.now());

  const CustomClearIndicator = ({ innerProps, selectProps }: any) => (
    <div className={cn('custom-clear-indicator absolute right-7 me-2')} {...innerProps}>
      {selectProps.value && (
        <X size={16} color='var(--dark_5)' onClick={selectProps.clearValue}/>
      )}
    </div>
  );

  const CustomDropdownIndicator = ({ innerProps, isFocused }: any) => (
    <div className={cn('custom-dropdown-indicator me-2')} {...innerProps}>
      {isFocused ? <ChevronUp size={20} color='var(--dark_5)'/> : <ChevronDown size={20} color='var(--dark_5)'/>}
    </div>
  );

  // const MenuList = (props:any) => {
  //   return (
  //     <div className={cn({
  //       'h-[300px]': props.children.length >= 8,
  //     })}>
  //       <PerfectScrollbar>
  //         {props.children}
  //       </PerfectScrollbar>
  //     </div>
  //   );
  // };

  return (
    <div className={`${width || 'w-full'}`}>
      <div className="group relative flex w-full items-center gap-4">
        <ReactSelect
          ref={ref}
          name={name}
          placeholder=""
          inputId={`${id.current}_${name}`}
          options={options}
          classNames={{
            control: (state) => {
              return cn('!rounded-lg !min-h-11 !w-full !text-sm !shadow-none !cursor-pointer border !border-dark_2 overflow-hidden', {
                'has-value-focused': state.hasValue && state.isFocused,
                'has-value': state.hasValue,
                'focused border !border-primary': state.isFocused,
                'border !border-danger': errors?.[name]?.message && !rest.isDisabled
              });
            },
            option: (state) => {
              return cn('!cursor-pointer !text-xs !flex !font-normal', {
                '!bg-primary !text-white': state.isSelected,
                '!bg-primary_lighter': state.isFocused && !state.isSelected
              });
            },
            singleValue: () => {
              return cn('!p-0.5 !text-dark !flex !text-xs group-has-[.has-value-focused]:!bg-primary_lighter');
            },
            multiValue: (state) => {
              return cn('!text-white !rounded !flex', {
                '!bg-primary': state.hasValue
              });
            },
            multiValueLabel: () => {
              return cn('!text-white !rounded !py-px');
            },
            menu: () => {
              return cn('!rounded border !border-none');
            },
            indicatorSeparator: () => {
              return cn('!hidden');
            },
            container: () => {
              return cn('!w-full');
            },
          }}
          components={{
            ClearIndicator: CustomClearIndicator,
            DropdownIndicator: CustomDropdownIndicator,
            // MenuList:MenuList
          }}
          debounceTimeout={rest.debounceTimeout || 500}
          {...rest}
        />

        <label htmlFor={`${id.current}`}
          className="pointer-events-none absolute flex translate-x-3 text-xs transition-all duration-200
            ease-in-out group-has-[.focused]:top-0 group-has-[.has-value-focused]:top-0 group-has-[.has-value]:top-0 group-has-[.focused]:-translate-y-2 group-has-[.has-value-focused]:-translate-y-2 group-has-[.has-value]:-translate-y-2 group-has-[.focused]:bg-white group-has-[.has-value-focused]:bg-white group-has-[.has-value]:bg-white"
        >
          <span className={cn('text-xs font-normal text-dark_4 group-has-[.focused]:text-primary group-has-[.has-value-focused]:!text-primary group-has-[.has-value]:text-dark', {
            '!text-danger': errors?.[name]?.message && !rest.isDisabled ,
            'group-has-[.has-value-focused]:!text-danger': errors?.[name]?.message && !rest.isDisabled
          })}
          >
            {label}
          </span>
          {isRequired && <span> {IsRequiredIcon} </span>}
        </label>

        {!!tooltipText && (
          <div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info size={18} color="var(--dark_5)" />
                </TooltipTrigger>
                <TooltipContent>
                  {tooltipText}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>

      {!!infoText && (
        <div className="mx-2.5 mt-2 flex items-center gap-2 align-middle">
          <span>
            <Info size={16} color="var(--dark_5)" />
          </span>
          <span className="text-xs text-dark_5">{infoText}</span>
        </div>
      )}

      {errors?.[name]?.message && !rest.isDisabled && (
        <div className="mx-2 mt-2 flex items-center gap-2 align-middle">
          <span className='h-full'>
            <img src={errorIcon} height={14} width={14} alt='Error' />{' '}
          </span>
          <span className="text-xs text-danger"> {errors?.[name]?.message} </span>
        </div>
      )}
    </div>
  );
}
);

FloatingDropdown.displayName = 'FloatingDropdown';
export default memo(FloatingDropdown);
